// Carousel backgrounds.
//
// A fixed set of vector patterns drawn with the palette colors. Each one shifts a little from
// slide to slide (unless "vary" is off), so the carousel feels like one continuous piece when
// you swipe. The person can also upload their own image, drawn with a color overlay so the
// text stays readable.

import { CANVAS_W, CANVAS_H, PAD, roundRect } from "./canvas";
import { withAlpha } from "./palettes";

// Order shown in the Background panel. The labels are translated in the UI.
export const BG_STYLE_IDS = ["solid", "lines", "polygons", "waves", "blobs", "frame"];

export const DEFAULT_BG_STYLE_ID = "solid";

// Pattern settings the person can change. These defaults give the standard look.
export const DEFAULT_BG_OPTIONS = {
  intensity: 100, // % multiplier on the pattern opacity
  size: 100, // % scale of the shapes
  density: 100, // % amount of shapes (rings, facets, layers, frames...)
  seed: 0, // 0 = default arrangement; Shuffle increases it
  mirror: "none", // "none" | "h" | "v" | "hv"
  color: "accent", // which color leads the pattern: "accent" | "text"
  vary: true, // shapes shift from slide to slide
};

// % of the palette background laid over an uploaded image.
export const DEFAULT_BG_OVERLAY = 55;

// Same seed, same numbers: a slide always draws the same shapes.
function seededRandom(seed) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Like CSS object-fit: cover. Fills the slide and crops the overflow without stretching.
function drawCover(ctx, bitmap, dw, dh) {
  const scale = Math.max(dw / bitmap.width, dh / bitmap.height);
  const sw = dw / scale;
  const sh = dh / scale;
  ctx.drawImage(bitmap, (bitmap.width - sw) / 2, (bitmap.height - sh) / 2, sw, sh, 0, 0, dw, dh);
}

// theme: the slide colors from buildTheme(). bgBitmap: uploaded image or null.
export function drawBackground(ctx, { style, theme, index = 0, bgBitmap = null, bgOverlay = DEFAULT_BG_OVERLAY, options }) {
  const o = { ...DEFAULT_BG_OPTIONS, ...(options || {}) };
  ctx.fillStyle = theme.bg;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

  // Uploaded image + overlay in the background color. No pattern on top.
  if (bgBitmap) {
    drawCover(ctx, bgBitmap, CANVAS_W, CANVAS_H);
    ctx.fillStyle = withAlpha(theme.bg, bgOverlay / 100);
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    return;
  }

  const i = o.vary ? index : 0;
  const k = o.intensity / 100;
  const sc = o.size / 100;
  const dn = o.density / 100;
  const sj = o.seed ? 1 : 0; // seed 0 keeps the default arrangement
  const rs = seededRandom(97 + o.seed * 31);
  const jit = (amp) => (rs() - 0.5) * amp * sj;
  const c1 = o.color === "text" ? theme.text : theme.accent;
  const c2 = o.color === "text" ? theme.accent : theme.text;
  const A = (c, a) => withAlpha(c, Math.min(1, a * k));
  const circle = (x, y, r) => {
    ctx.beginPath();
    ctx.arc(x, y, Math.max(1, r), 0, Math.PI * 2);
  };

  ctx.save();
  if (o.mirror === "h" || o.mirror === "hv") {
    ctx.translate(CANVAS_W, 0);
    ctx.scale(-1, 1);
  }
  if (o.mirror === "v" || o.mirror === "hv") {
    ctx.translate(0, CANVAS_H);
    ctx.scale(1, -1);
  }

  if (style === "blobs") {
    // Large soft circles, one outlined ring, and extra circles above 100% density.
    ctx.fillStyle = A(c1, 0.16);
    circle(CANVAS_W - 40 - i * 70 + jit(220), 150 + (i % 2) * 70 + jit(160), 400 * sc);
    ctx.fill();

    if (dn >= 0.3) {
      ctx.fillStyle = A(c2, 0.08);
      circle(60 + i * 60 + jit(220), CANVAS_H - 90 - (i % 3) * 40 + jit(160), 310 * sc);
      ctx.fill();
    }
    if (dn >= 0.5) {
      ctx.strokeStyle = A(c1, 0.45);
      ctx.lineWidth = 10;
      circle(CANVAS_W * 0.82 - (i % 3) * 50 + jit(200), CANVAS_H * 0.74 + jit(200), 110 * sc);
      ctx.stroke();
    }
    const extras = Math.round(Math.max(0, dn - 1) * 6);
    const er = seededRandom(200 + o.seed * 17 + i * 3);
    for (let e = 0; e < extras; e++) {
      ctx.fillStyle = A(er() > 0.5 ? c1 : c2, 0.08 + er() * 0.1);
      circle(er() * CANVAS_W, er() * CANVAS_H, (40 + er() * 120) * sc);
      ctx.fill();
    }
  } else if (style === "polygons") {
    // Low-poly facets: a jittered triangle mesh, stronger toward the bottom-right
    // corner so the text area stays clean.
    const rnd = seededRandom(11 + i * 7 + o.seed * 101);
    const cols = Math.max(2, Math.min(10, Math.round(5 * dn)));
    const rows = Math.max(3, Math.min(14, Math.round(7 * dn)));
    const jitter = Math.max(0.15, Math.min(1.1, 0.7 * sc));
    const pts = [];
    for (let r = 0; r <= rows; r++) {
      pts.push([]);
      for (let c = 0; c <= cols; c++) {
        const onEdgeX = c === 0 || c === cols;
        const onEdgeY = r === 0 || r === rows;
        const jx = onEdgeX ? 0 : (rnd() - 0.5) * (CANVAS_W / cols) * jitter;
        const jy = onEdgeY ? 0 : (rnd() - 0.5) * (CANVAS_H / rows) * jitter;
        pts[r].push([(c * CANVAS_W) / cols + jx, (r * CANVAS_H) / rows + jy]);
      }
    }
    const tri = (p, q, t) => {
      const cx = (p[0] + q[0] + t[0]) / 3;
      const cy = (p[1] + q[1] + t[1]) / 3;
      const weight = (cx / CANVAS_W + cy / CANVAS_H) / 2; // 0 top-left, 1 bottom-right
      const base = 0.04 + rnd() * 0.18;
      const color = rnd() > 0.45 ? c1 : c2;
      ctx.fillStyle = A(color, base * (0.2 + weight * 1.3));
      ctx.beginPath();
      ctx.moveTo(p[0], p[1]);
      ctx.lineTo(q[0], q[1]);
      ctx.lineTo(t[0], t[1]);
      ctx.closePath();
      ctx.fill();
    };
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const p00 = pts[r][c];
        const p10 = pts[r][c + 1];
        const p01 = pts[r + 1][c];
        const p11 = pts[r + 1][c + 1];
        tri(p00, p10, p01);
        tri(p10, p11, p01);
      }
    }
  } else if (style === "lines") {
    // Concentric rings from the top-right corner, diagonal hatching bottom-left,
    // and one bold accent stroke.
    const rings = Math.max(2, Math.round(8 * dn));
    const step = 72 * sc;
    const ringX = CANVAS_W - ((i + o.seed) % 3) * 60;
    ctx.strokeStyle = A(c1, 0.24);
    ctx.lineWidth = 3;
    for (let r = 0; r < rings; r++) {
      circle(ringX, 0, 150 + r * step);
      ctx.stroke();
    }

    const hatch = 520 * sc;
    const spacing = 26 / Math.max(0.4, dn);
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(0, CANVAS_H);
    ctx.lineTo(0, CANVAS_H - hatch);
    ctx.lineTo(hatch, CANVAS_H);
    ctx.closePath();
    ctx.clip();
    ctx.strokeStyle = A(c2, 0.22);
    ctx.lineWidth = 3;
    for (let x = -hatch; x < hatch * 2; x += spacing) {
      ctx.beginPath();
      ctx.moveTo(x, CANVAS_H);
      ctx.lineTo(x + hatch, CANVAS_H - hatch);
      ctx.stroke();
    }
    ctx.restore();

    ctx.strokeStyle = withAlpha(c1, Math.min(1, k));
    ctx.lineWidth = 10;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(PAD, CANVAS_H - 46);
    ctx.lineTo(PAD + 160, CANVAS_H - 46);
    ctx.stroke();
  } else if (style === "waves") {
    // Layered sine waves along the bottom.
    const layers = Math.max(1, Math.min(6, Math.round(3 * dn)));
    const region = 0.15 * sc;
    const alphas = [0.2, 0.1, 0.32, 0.18, 0.26, 0.14];
    for (let l = 0; l < layers; l++) {
      const baseY = CANVAS_H * (1 - region + region * (l / Math.max(layers - 1, 1)) * (2 / 3));
      const amp = Math.max(8, 38 - l * 8) * sc;
      const phase = i * 0.9 + l * 1.4 + o.seed * 0.7;
      ctx.fillStyle = A(l % 2 === 1 ? c2 : c1, alphas[l]);
      ctx.beginPath();
      ctx.moveTo(0, CANVAS_H);
      for (let x = 0; x <= CANVAS_W; x += 12) {
        ctx.lineTo(x, baseY + Math.sin((x / CANVAS_W) * Math.PI * 2 + phase) * amp);
      }
      ctx.lineTo(CANVAS_W, CANVAS_H);
      ctx.closePath();
      ctx.fill();
    }
  } else if (style === "frame") {
    // Nested rounded frames, a soft circle and two accent bars. The circle
    // and the bars move a little from slide to slide (when "vary" is on).
    const shift = (i % 3) * 140;
    ctx.fillStyle = A(c1, 0.08);
    circle(
      CANVAS_W - 120 - shift + jit(200),
      CANVAS_H - 160 - (i % 2) * 220 + jit(200),
      340 * sc
    );
    ctx.fill();

    const frames = Math.max(1, Math.round(dn));
    for (let f = 0; f < frames; f++) {
      const inset = 36 + f * 28;
      ctx.strokeStyle = A(c1, Math.max(0.15, 0.55 - f * 0.15));
      ctx.lineWidth = Math.max(2, 4 * sc);
      roundRect(ctx, inset, inset, CANVAS_W - inset * 2, CANVAS_H - inset * 2, Math.max(8, 36 - f * 6));
      ctx.stroke();
    }

    const barShift = (i % 3) * 90;
    ctx.fillStyle = withAlpha(c1, Math.min(1, k));
    ctx.fillRect(CANVAS_W - 36 - 220 - barShift, 36 - 7 * sc, 180, 14 * sc);
    ctx.fillRect(36 + 40 + barShift, CANVAS_H - 36 - 7 * sc, 120, 14 * sc);
  } else {
    // "solid": only the top bar
    ctx.fillStyle = withAlpha(c1, Math.min(1, k));
    ctx.fillRect(0, 0, CANVAS_W, 14 * sc);
  }

  ctx.restore();
}